'use client';

import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { use, useMemo } from 'react';
import ProfilePreviewCta from '@/app/(auth-routes)/(blocked-by-onboarding)/(refugees)/dashboard/_components/ProfilePreviewCta';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { typographyVariants } from '@/components/ui/typography';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { OnboardingDrawerContext } from './context/OnboardingDrawerContext';
import { useOnboardingSteps } from './hooks/useOnboardingSteps';
import OnboardingChecklist from './OnboardingChecklist';
export default function OnboardingDrawer() {
  const t = useTranslations('Dashboard.ProfileCompletion');
  const { isOpen, onOpenChange } = use(OnboardingDrawerContext);
  const { completionPercentage } = useOnboardingSteps();
  const [isMobile] = useIsMobile();
  const pathname = usePathname();
  const isClickOnBannerWillScrollToProfileCompletion = useMemo(() => {
    return pathname?.includes('/dashboard') && isMobile;
  }, [pathname, isMobile]);

  const isShowBanner = useMemo(() => {
    const excludedPaths = [
      '/inbox',
      '/profile',
      '/edit-profile',
      '/account-settings',
      '/verify-user',
      '/language-program',
      '/notifications',
    ];
    return !excludedPaths.some((path) => pathname?.includes(path));
  }, [pathname]);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        {isClickOnBannerWillScrollToProfileCompletion ? (
          <Banner scrollIntoView={true} />
        ) : (
          isShowBanner && (
            <SheetTrigger asChild>
              <button>
                <Banner />
              </button>
            </SheetTrigger>
          )
        )}
        <SheetContent side='left' aria-describedby={undefined}>
          <SheetHeader>
            <SheetTitle>
              <span className='sr-only'>{t('profile_completion')}</span>
            </SheetTitle>
          </SheetHeader>
          <div className='mt-10 flex min-h-[calc(100%-40px)] flex-col items-center text-center'>
            <Image
              src='/images/group-of-people.svg'
              alt='Profile Completion'
              width={262}
              height={170}
              className='m-8'
            />
            <h2 className={`${typographyVariants({ variant: 'h2' })} mb-1`}>
              {t('complete_your_profile')}
            </h2>
            {completionPercentage !== 100 ? (
              <>
                <h3
                  className={`${typographyVariants({ variant: 'h4' })} px-4 font-normal`}
                >
                  {isMobile ? t('short_description') : t('description')}
                </h3>
                <OnboardingChecklist onOpenChange={onOpenChange} />
              </>
            ) : (
              <p className='mb-auto text-neutral-600'>
                {t('completed_message')}
              </p>
            )}
            <Image
              src='/images/dashboard/profile-completion-flower.svg'
              alt='Profile Completion'
              width={50}
              height={41}
              className='mt-16 mb-2'
            />
            <ProfilePreviewCta layout='drawer' />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
const Banner = ({ scrollIntoView = false }: { scrollIntoView?: boolean }) => {
  const t = useTranslations('Dashboard.ProfileCompletion');
  const { completionPercentage } = useOnboardingSteps();
  if (completionPercentage === 100) {
    return null;
  }
  const handleClick = () => {
    const element = document.querySelector('#profile-completion-section');
    element?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <div
      id='onboarding-drawer-banner'
      className='fixed right-0 bottom-0 my-6 w-full px-6 text-center md:w-auto'
      onClick={scrollIntoView ? handleClick : undefined}
    >
      <div className='bg-white-200 h-fit w-full rounded-xl px-6 py-4 shadow-lg focus-visible:outline-hidden md:max-w-[218px] md:rounded-lg md:p-8'>
        <h3
          className={cn(
            typographyVariants({
              variant: 'h4',
            }),
            'mb-1 font-medium md:mb-4 md:text-base'
          )}
        >
          {t('profile_completion')}
        </h3>
        <div className='bg-white-400 relative h-6 w-full overflow-hidden rounded-full'>
          <div
            className='from-secondary-100 to-secondary-600 absolute top-0 left-0 h-full rounded-full bg-linear-to-r transition-all duration-500'
            style={{ width: `${completionPercentage}%` }}
            role='progressbar'
            aria-valuenow={completionPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
          <div className='absolute inset-0 flex items-center justify-center'>
            <span
              className={`text-sm font-medium ${completionPercentage > 50 ? 'text-white' : 'text-neutral-600'}`}
            >
              {completionPercentage}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
